import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import { ApiError } from '../../../lib/http/ApiError';
import {
  appendFilterParams,
  resolveDbSortBy,
} from '../../../components/ui/tableTemplate/tableUtils';
import type {
  FilterState,
  PaginationMeta,
  SortState,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { INSTANCE_STATUS, REGISTRATION_KEY_STATUS } from '../instanceTypes';
import type {
  InstanceRow,
  RegistrationKey,
  RegistrationKeyStatus,
  InstanceKeyStatus,
  GeneratedKey,
  Terrain,
  CreateInstancePayload,
  UpdateInstancePayload,
} from '../instanceTypes';
import type { RawInstanceRow, RawRegistrationKey, RawGeneratedKey } from './instanceApi.types';
import INSTANCE_ENDPOINTS from './instanceEndpoints';

function parseStatus<T extends string>(
  raw: string | undefined,
  values: readonly T[],
  fallback: T,
): T {
  const up = raw?.toUpperCase() ?? '';
  return (values as readonly string[]).includes(up) ? (up as T) : fallback;
}

/** Known key statuses (uppercased), hoisted so list mapping doesn't re-allocate per row. */
const KNOWN_KEY_STATUSES = new Set<string>(Object.values(REGISTRATION_KEY_STATUS));

/**
 * Maps the server `current_key_status` to the displayed key status:
 * `null`/absent → `null` (no key); a recognised value → that status;
 * anything else → `'UNKNOWN'` (surfaced as such rather than mislabelled, e.g. as
 * a misleading `EXPIRED`).
 */
function toKeyStatus(raw: string | null | undefined): InstanceKeyStatus | null {
  if (raw == null) return null;
  const up = raw.toUpperCase();
  return KNOWN_KEY_STATUSES.has(up) ? (up as RegistrationKeyStatus) : 'UNKNOWN';
}

function toInstanceRow(raw: RawInstanceRow): InstanceRow {
  return {
    id: raw.id,
    organizationId: raw.organization_id,
    name: raw.instance_name ?? '',
    computerName: raw.computer_name,
    email: raw.email,
    path: raw.path,
    codeterrain: [...new Set(raw.codeterrain)],
    version: raw.version,
    genVersion: raw.gen_version ?? null,
    pays: raw.pays ?? null,
    registeredAt: raw.registered_at,
    status: parseStatus(
      raw.status,
      Object.values(INSTANCE_STATUS) as InstanceRow['status'][],
      'UNREGISTERED',
    ),
    keyStatus: toKeyStatus(raw.current_key_status),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

function toRegistrationKey(raw: RawRegistrationKey): RegistrationKey {
  return {
    id: raw.id,
    hint: raw.hint,
    // not a blocker: falls back to 'EXPIRED' for an unrecognised status (only
    // reachable on an API contract violation). `toKeyStatus` above now uses
    // 'UNKNOWN' for that case; aligning this would mean widening
    // RegistrationKey.status to include 'UNKNOWN' and handling it in the key
    // detail rendering (KeyCard). Pre-existing, out of scope here → follow-up.
    status: parseStatus(
      raw.status,
      Object.values(REGISTRATION_KEY_STATUS) as RegistrationKey['status'][],
      'EXPIRED',
    ),
    expiresAt: raw.expires_at,
    createdAt: raw.created_at,
    usedAt: raw.used_at,
    revokedAt: raw.revoked_at,
  };
}

function toGeneratedKey(raw: RawGeneratedKey): GeneratedKey {
  return {
    key: raw.key,
    hint: raw.hint,
    expiresAt: raw.expires_at,
  };
}

/** Paginated instances response: the current page of rows + pagination meta. */
export type InstancesResponse = { data: InstanceRow[]; meta: PaginationMeta };

/**
 * Meta for a non-paginated (bare-array or empty) response: the whole list is a
 * single page. Never derived from the requested page/limit — that would
 * fabricate phantom pages for data that is already fully displayed.
 */
function singlePageMeta(total: number): PaginationMeta {
  return {
    total,
    page: 1,
    limit: total || 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  };
}

export function InstanceApiFactory(client: FetchClient) {
  return {
    getAll: async (
      filters?: FilterState<InstanceRow>,
      columns?: TableColumn<InstanceRow>[],
      sortState?: SortState<InstanceRow> | null,
      page?: number,
      limit?: number,
      opts?: RequestOptions,
    ): Promise<InstancesResponse> => {
      const qs = new URLSearchParams();
      if (filters && columns) appendFilterParams(qs, filters, columns);
      if (sortState?.sortBy != null && columns) {
        qs.set('sortBy', resolveDbSortBy(String(sortState.sortBy), columns));
        qs.set('sortOrder', sortState.sortOrder);
      }
      // Server-side pagination — omitted when not provided so the server applies
      // its own defaults.
      if (page != null) qs.set('page', String(page));
      if (limit != null) qs.set('limit', String(limit));
      const query = qs.toString();
      const res = await client.get<
        RawInstanceRow[] | { data: RawInstanceRow[]; meta?: PaginationMeta }
      >(`${INSTANCE_ENDPOINTS.BASE}${query ? `?${query}` : ''}`, opts);
      // Tolerate a bare-array response (non-paginated full list) by
      // synthesizing a single-page meta; `res` may also be null on a 2xx
      // without a body.
      if (Array.isArray(res)) {
        const data = res.map(toInstanceRow);
        return { data, meta: singlePageMeta(data.length) };
      }
      const data = (res?.data ?? []).map(toInstanceRow);
      return { data, meta: res?.meta ?? singlePageMeta(data.length) };
    },

    getById: async (id: string, opts?: RequestOptions): Promise<InstanceRow> => {
      const raw = await client.get<RawInstanceRow>(INSTANCE_ENDPOINTS.BY_ID(id), opts);
      return toInstanceRow(raw);
    },

    create: async (data: CreateInstancePayload, opts?: RequestOptions): Promise<InstanceRow> => {
      const raw = await client.post<RawInstanceRow>(INSTANCE_ENDPOINTS.BASE, data, opts);
      return toInstanceRow(raw);
    },

    update: async (
      id: string,
      data: UpdateInstancePayload,
      opts?: RequestOptions,
    ): Promise<InstanceRow> => {
      const raw = await client.patch<RawInstanceRow>(INSTANCE_ENDPOINTS.BY_ID(id), data, opts);
      return toInstanceRow(raw);
    },

    delete: (id: string, opts?: RequestOptions): Promise<void> =>
      client.delete(INSTANCE_ENDPOINTS.BY_ID(id), opts),

    listKeys: async (instanceId: string, opts?: RequestOptions): Promise<RegistrationKey[]> => {
      const res = await client.get<RawRegistrationKey[] | { data: RawRegistrationKey[] }>(
        INSTANCE_ENDPOINTS.REGISTRATION_KEYS(instanceId),
        opts,
      );
      const raw = Array.isArray(res) ? res : (res?.data ?? []);
      return raw.map(toRegistrationKey);
    },

    generateKey: async (instanceId: string, opts?: RequestOptions): Promise<GeneratedKey> => {
      const raw = await client.post<RawGeneratedKey>(
        INSTANCE_ENDPOINTS.REGISTRATION_KEYS(instanceId),
        {},
        opts,
      );
      return toGeneratedKey(raw);
    },

    getActiveKey: async (instanceId: string, opts?: RequestOptions): Promise<RegistrationKey[]> => {
      const res = await client.get<RawRegistrationKey[] | { data: RawRegistrationKey[] }>(
        `${INSTANCE_ENDPOINTS.REGISTRATION_KEYS(instanceId)}?status[eq]=active`,
        opts,
      );
      const raw = Array.isArray(res) ? res : (res?.data ?? []);
      return raw.map(toRegistrationKey);
    },

    revokeKey: async (instanceId: string, keyId: string, opts?: RequestOptions): Promise<void> => {
      try {
        await client.delete(INSTANCE_ENDPOINTS.REGISTRATION_KEY_BY_ID(instanceId, keyId), opts);
      } catch (err) {
        if (ApiError.isApiError(err) && err.statusCode === 409) {
          throw new ApiError(
            {
              message: err.messages[0] ?? 'Key already consumed',
              statusCode: 409,
              error: 'REGISTRATION_KEY_ALREADY_CONSUMED',
            },
            409,
          );
        }
        throw err;
      }
    },

    getTerrains: async (opts?: RequestOptions): Promise<Terrain[]> => {
      // The /terrains endpoint is paginated with a default limit of 10, so a bare
      // request returns only the first 10 terrains. We need the full list (it feeds
      // filter option sets), so fetch every page: request the max page size (backend
      // caps `limit` at 100) and follow `meta.totalPages`. Still tolerates a plain
      // array response (non-paginated) for forward-compat.
      const PAGE_SIZE = 100;
      type TerrainsPage = { data: Terrain[]; meta?: { totalPages?: number } };
      const fetchPage = (page: number) =>
        client.get<Terrain[] | TerrainsPage>(
          `${INSTANCE_ENDPOINTS.TERRAINS}?limit=${PAGE_SIZE}&page=${page}`,
          opts,
        );

      // Alphabetical by codeterrain (locale-aware): every consumer (instances
      // filter, NewInstanceModal, PayslipsTable) displays the code first, so a
      // label-based order would look shuffled.
      const byCode = (a: Terrain, b: Terrain) => a.codeterrain.localeCompare(b.codeterrain);

      const first = await fetchPage(1);
      if (Array.isArray(first)) return [...first].sort(byCode);

      // Symmetric fail-loud with the pages ≥ 2 guard below: a page-1 envelope
      // without a data array (or a null body) would silently drop page 1 from
      // an aggregation whose remaining pages are well-formed.
      if (!Array.isArray(first?.data)) {
        throw new Error('getTerrains: page 1 returned a non-paginated response');
      }

      const all: Terrain[] = [...first.data];
      const totalPages = first.meta?.totalPages ?? 1;
      for (let page = 2; page <= totalPages; page++) {
        const res = await fetchPage(page);
        // A shape change mid-loop (bare array, empty body, envelope without
        // data) would silently truncate the list — the exact bug this
        // aggregation exists to prevent. Fail loud instead.
        if (Array.isArray(res) || !Array.isArray(res?.data)) {
          throw new Error(
            `getTerrains: page ${page}/${totalPages} returned a non-paginated response`,
          );
        }
        all.push(...res.data);
      }
      return all.sort(byCode);
    },
  };
}
